/**
 * /ndt-inspection/sources/:id — one sealed source: its current activity, leak
 * test, custody, the append-only custody log, and a decay lookup by date
 * (GET /sources/:id, POST /sources/:id/logs, GET /sources/:id/decay).
 *
 * EVERY FIGURE IS THE API'S. Current activity and the activity on a looked-up
 * day are decayed server-side and arrive in `meta.units.activity`.
 *
 * CUSTODY ACTIONS. Check out when nobody holds it; check in or transfer when
 * someone does. A field worker may only take a source themselves and only
 * act on a source they hold, so a transfer (which hands it to someone else)
 * is not offered to them. The API is the authority either way: its refusal
 * codes (already_checked_out, not_checked_out, not_in_service, same_holder,
 * not_self, not_holder, and the shared register's source_closed / source_lost)
 * render as plain sentences in the dialog.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRightLeft, Atom, LogIn, LogOut, Pencil, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteNdtSource, useNdtDecay, useNdtSource } from '../../hooks/useNdtInspection';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminNdt,
  canLogNdtSource,
  daysHint,
  formatNdtActivity,
  isNdtDayInput,
  ndtLicenceLabel,
  formatNdtValue,
  ndtErrorMessage,
  ndtEventLabel,
  ndtLeakTestSkin,
  ndtSourceStatusSkin,
  ndtSourceWriteErrorMessage,
} from '../../lib/ndt-inspection';
import type { NdtSourceEvent, NdtSourceLog } from '../../types/ndt-inspection';
import { ConfirmDialog, SourceDialog, SourceLogDialog } from './NdtDialogs';
import { isNotFound, NDT_DATE_INPUT, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtField, NdtLoading, NdtPanel, NdtSpec, ndtDay } from './NdtBits';
import { custodyText } from './SourcesScreen';

/**
 * A custody row's survey reading in the tenant's unit (api#1142: mR/h on an
 * imperial tenant, µSv/h on a metric one), as the API sent it — nothing here
 * converts. A read from before api#1142 carries only the canonical µSv/h.
 */
export function surveyOf(l: NdtSourceLog): { value: number | null; unit: string } {
  if (l.surveyReadingUnit) return { value: l.surveyReading ?? null, unit: l.surveyReadingUnit };
  return { value: l.surveyReadingUsvH, unit: 'µSv/h' };
}

export default function SourceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const query = useNdtSource(id);
  const remove = useDeleteNdtSource();
  const [editing, setEditing] = useState(false);
  const [event, setEvent] = useState<NdtSourceEvent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [decayOn, setDecayOn] = useState('');
  const decay = useNdtDecay(id, decayOn);

  const source = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units = meta?.units ?? null;
  const tz = meta?.timeZone ?? null;
  // US spelling for a US or imperial tenant; Canadian otherwise. From what the
  // page already holds (the session's company, the API's unit system) — no fetch.
  const licenceLabel = ndtLicenceLabel(company?.country, meta?.unitSystem);
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const role = user?.role;
  const selfId: string | null = user?.id ?? null;
  const isField = role === 'field_worker';
  const canLog = canLogNdtSource(role) && !!source;
  const canEdit = canAdminNdt(role) && !!source && !!units;
  const out = !!source?.custody.checkedOut;
  const holdsIt = !!source && source.custody.holderUserId === selfId;
  const logs = source?.logs ?? [];
  const surveyUnit = logs.length > 0 ? surveyOf(logs[0]).unit : 'µSv/h';

  const actions: { event: NdtSourceEvent; label: string; icon: React.ReactNode }[] = [];
  if (canLog && source) {
    if (!out) actions.push({ event: 'check_out', label: 'Check out', icon: <LogOut className="size-4" aria-hidden="true" /> });
    else if (!isField || holdsIt) {
      actions.push({ event: 'check_in', label: 'Check in', icon: <LogIn className="size-4" aria-hidden="true" /> });
      if (!isField) actions.push({ event: 'transfer', label: 'Transfer', icon: <ArrowRightLeft className="size-4" aria-hidden="true" /> });
    }
  }
  const decayFailure = queryLoadFailure(decay);
  const decayErr = decayFailure ? ndtErrorMessage(queryFailureError(decay), 'The activity could not be looked up.') : null;

  return (
    <div data-testid="ndt-source-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection · Source"
        title={source ? `${source.isotopeLabel || source.isotope} · ${source.serialNumber}` : query.isLoading ? 'Source' : '—'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          actions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {actions.map((a, i) => (
                <button
                  key={a.event}
                  type="button"
                  onClick={() => setEvent(a.event)}
                  className={i === 0 ? 'fi-action fi-action--standard fi-action--primary' : 'fi-action fi-action--standard border border-white/25 hover:border-white/60'}
                >
                  {a.icon}
                  {a.label}
                </button>
              ))}
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Atom className="size-4" aria-hidden="true" />,
            label: 'Activity',
            sub: source ? `As of ${ndtDay(source.currentActivityOn)}` : undefined,
            tone: 'neutral',
            loading: query.isLoading,
            value: formatNdtActivity(source?.currentActivity, units?.activity),
          },
          {
            icon: <UserRound className="size-4" aria-hidden="true" />,
            label: 'Custody',
            tone: out ? 'info' : 'neutral',
            loading: query.isLoading,
            value: source ? custodyText(source) : '—',
          },
          {
            icon: <ShieldCheck className="size-4" aria-hidden="true" />,
            label: 'Leak test',
            // A failed test is out of use whatever its due day says, so no "in N days" under "Failed".
            sub: source && source.leakTest.status !== 'failed' ? daysHint(source.leakTest.daysLeft, 'overdue') ?? undefined : undefined,
            tone: source?.leakTest.status === 'valid' ? 'success' : source?.leakTest.status === 'none' ? 'neutral' : 'accent',
            loading: query.isLoading,
            value: source ? ndtLeakTestSkin(source.leakTest.status).label : '—',
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading source" />
          ) : notFound || (!failure && !source) ? (
            <NdtPanel eyebrow="Source" title="Source">
              <NdtEmpty>This source was not found. It may have been deleted.</NdtEmpty>
            </NdtPanel>
          ) : failure || !source ? (
            <NdtPanel eyebrow="Source" title="Source">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this source" />
            </NdtPanel>
          ) : (
            <>
              <NdtPanel
                eyebrow="Source"
                title="Details"
                testId="ndt-source-details"
                actions={
                  canEdit ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setEditing(true)}>
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit
                    </button>
                  ) : undefined
                }
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <NdtSpec label="Isotope" value={source.isotopeLabel || source.isotope} />
                  <NdtSpec label="Half-life" value={source.halfLifeDays != null ? `${formatNdtValue(source.halfLifeDays, null)} days` : '—'} />
                  <NdtSpec label="Serial number" value={source.serialNumber || '—'} mono />
                  <NdtSpec label="Status" value={<NdtBadge skin={ndtSourceStatusSkin(source.status)} value={source.status} />} />
                  <NdtSpec label="Reference activity" value={formatNdtValue(source.referenceActivity, units?.activity)} mono />
                  <NdtSpec label="Reference date" value={ndtDay(source.referenceOn)} />
                  <NdtSpec label="Current activity" value={formatNdtActivity(source.currentActivity, units?.activity)} mono />
                  <NdtSpec label="Days since reference" value={source.elapsedDays != null ? String(source.elapsedDays) : '—'} mono />
                  <NdtSpec label="Exposure device" value={source.deviceName || '—'} />
                  <NdtSpec label={licenceLabel} value={source.licenceNumber || '—'} mono />
                  <NdtSpec label="Last leak test" value={ndtDay(source.leakTest.lastOn)} />
                  <NdtSpec
                    label="Leak test due"
                    value={
                      <span data-testid="ndt-leak-due" className="inline-flex flex-wrap items-center gap-2">
                        {ndtDay(source.leakTest.dueOn)}
                        <NdtBadge skin={ndtLeakTestSkin(source.leakTest.status)} value={source.leakTest.status} />
                      </span>
                    }
                  />
                  <NdtSpec label="Held by" value={custodyText(source)} />
                  <NdtSpec label="Last location" value={source.custody.location || '—'} />
                </dl>
                {source.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{source.notes}</p>}
              </NdtPanel>

              <NdtPanel eyebrow="Decay" title="Activity on a date" testId="ndt-decay">
                <div className="grid gap-4 px-4 py-4 sm:grid-cols-[minmax(0,14rem)_1fr] sm:items-end">
                  <NdtField label="Date">
                    <input type="date" className={NDT_DATE_INPUT} value={decayOn} min={source.referenceOn ?? undefined} onChange={(e) => setDecayOn(e.target.value)} />
                  </NdtField>
                  <div aria-live="polite" className="text-sm text-fieldiq-text">
                    {!decayOn ? (
                      <span className="text-fieldiq-muted">Pick a date to see the decayed activity.</span>
                    ) : !isNdtDayInput(decayOn) ? (
                      <span role="alert" data-testid="ndt-decay-invalid">Enter the date as YYYY-MM-DD, with a four-digit year.</span>
                    ) : decay.isLoading ? (
                      <span className="text-fieldiq-muted">Looking up…</span>
                    ) : decayErr ? (
                      <span role="alert">{decayErr}</span>
                    ) : decay.data?.data ? (
                      <span data-testid="ndt-decay-result">
                        <span className="font-mono text-base font-extrabold text-white">{formatNdtActivity(decay.data.data.activity, decay.data.data.unit)}</span>
                        {' '}on {ndtDay(decay.data.data.on)} · {decay.data.data.elapsedDays} days after reference
                      </span>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </NdtPanel>

              <NdtPanel
                eyebrow="Append-only record"
                title="Custody log"
                testId="ndt-source-log"
                footer={logs.length > 0 ? `${logs.length} event${logs.length === 1 ? '' : 's'}, newest first` : undefined}
              >
                {logs.length === 0 ? (
                  <NdtEmpty>This source has never been checked out.</NdtEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">When</th>
                          <th scope="col" className="px-4 py-3">Event</th>
                          <th scope="col" className="px-4 py-3">Holder</th>
                          <th scope="col" className="px-4 py-3">Location</th>
                          <th scope="col" className="px-4 py-3">Job ref</th>
                          {/* The header row is uppercase; the unit is not, or µSv/h reads MSV/H — a thousand times the figure. */}
                          <th scope="col" className="px-4 py-3 text-right">Survey <span data-testid="ndt-survey-unit" className="normal-case">({surveyUnit})</span></th>
                          <th scope="col" className="px-4 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((l) => (
                          <tr key={l.id} className="border-t border-fieldiq-border">
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstant(l.occurredAt, tz) ?? '—'}</td>
                            <td className="px-4 py-3 text-sm font-bold text-white">{ndtEventLabel(l.event)}</td>
                            {/* Only a check-out or transfer names a holder; on any other event the user is whoever recorded it. */}
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{l.event === 'check_out' || l.event === 'transfer' ? l.userName || '—' : '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{l.location || '—'}</td>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{l.jobRef || '—'}</td>
                            <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{(() => {
                              const r = surveyOf(l);
                              // The header names the unit; a row in another unit (never, from one response) says its own.
                              return formatNdtValue(r.value, r.unit === surveyUnit ? null : r.unit);
                            })()}</td>
                            <td className="max-w-[20rem] px-4 py-3 text-sm text-fieldiq-text">{l.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </NdtPanel>

              {canEdit && (
                <div className="flex justify-end">
                  <button type="button" className={NDT_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                    <Trash2 className="size-4" aria-hidden="true" />
                    Delete source
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {source && canEdit && <SourceDialog open={editing} onClose={() => setEditing(false)} source={source} units={units} />}
      {source && event && (
        <SourceLogDialog open={!!event} onClose={() => setEvent(null)} source={source} event={event} selfId={selfId} selfOnly={isField} />
      )}
      {source && canEdit && (
        <ConfirmDialog
          open={deleting}
          title="Delete source"
          body={`Remove ${source.isotopeLabel || source.isotope} ${source.serialNumber} from the register? A checked-out source must be checked in first.`}
          confirmLabel="Delete"
          pending={remove.isPending}
          error={deleteError}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            remove.mutate(source.id, {
              onSuccess: () => {
                toast.success('Source deleted');
                navigate('/ndt-inspection/sources', { replace: true });
              },
              onError: (err) => setDeleteError(ndtSourceWriteErrorMessage(err, 'The source could not be deleted.')),
            })
          }
        />
      )}
    </div>
  );
}
